/**
 * Chunked Resumable Upload Utility for Google Cloud Storage
 *
 * Uses the GCS resumable upload protocol to upload large video files
 * reliably from the browser. Files are split into chunks and each chunk
 * is uploaded in a separate HTTP request, allowing:
 *   - Resume on network failure (only re-upload the failed chunk)
 *   - Accurate per-chunk progress tracking
 *   - No timeout issues (each request is short-lived)
 *   - Support for files of any size
 *
 * Security: The resumable session URI is scoped to a single file upload
 * and expires after ~1 week of inactivity. No credentials are exposed
 * to the browser — the backend creates the session server-side.
 */

const MAX_RETRIES = 5;
const RETRY_BASE_DELAY_MS = 1000;

/**
 * Returns an optimal chunk size (in bytes) based on the file size.
 * GCS requires chunks to be multiples of 256 KiB (except the final chunk).
 */
function getChunkSize(fileSize) {
  const KB = 1024;
  const MB = 1024 * KB;

  if (fileSize <= 5 * MB) return fileSize;       // ≤5 MB  → single request
  if (fileSize <= 50 * MB) return 5 * MB;         // ≤50 MB → 5 MB chunks
  if (fileSize <= 500 * MB) return 10 * MB;       // ≤500 MB→ 10 MB chunks
  return 16 * MB;                                  // >500 MB→ 16 MB chunks
}

/**
 * Sleep helper for retry back-off.
 */
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Determine whether an error is worth retrying.
 */
function isRetryableError(error) {
  if (error instanceof TypeError) return true; // network failure
  const msg = error?.message?.toLowerCase() || "";
  return msg.includes("network") || msg.includes("failed to fetch");
}

/**
 * Determine whether an HTTP status code is worth retrying.
 */
function isRetryableStatus(status) {
  return status === 408 || status === 429 || (status >= 500 && status < 600);
}

function readUploadStatus(response, total) {
  if (response.status === 200 || response.status === 201) {
    return { complete: true, bytesReceived: total };
  }
  if (response.status !== 308) {
    const error = new Error(`Upload failed: ${response.status} ${response.statusText}`);
    error.status = response.status;
    throw error;
  }
  // GCS reports only persisted bytes. A missing Range means none were saved.
  const range = response.headers.get("range");
  if (!range) return { complete: false, bytesReceived: 0 };
  const match = /^bytes=0-(\d+)$/i.exec(range.trim());
  const bytesReceived = match ? Number(match[1]) + 1 : NaN;
  if (!Number.isSafeInteger(bytesReceived) || bytesReceived <= 0 || bytesReceived >= total) {
    throw new Error("Invalid upload acknowledgement from storage.");
  }
  return { complete: false, bytesReceived };
}

/**
 * Upload a single chunk with automatic retry + exponential back-off.
 *
 * @returns {{ complete: boolean, bytesReceived: number }}
 */
async function uploadChunkWithRetry(uri, chunk, start, end, total) {
  let lastError;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      // A failed response may still have persisted some or all of the chunk.
      if (attempt > 0) {
        const progress = await queryUploadProgress(uri, total);
        if (progress.complete || progress.bytesReceived > start) return progress;
        if (progress.bytesReceived < start) {
          throw new Error("Storage did not acknowledge previously uploaded data.");
        }
      }
      const response = await fetch(uri, {
        method: "PUT",
        headers: {
          "Content-Range": `bytes ${start}-${end}/${total}`,
        },
        body: chunk,
      });

      // 200 / 201 → upload complete
      if ([200, 201, 308].includes(response.status)) {
        return readUploadStatus(response, total);
      }

      // Retryable server error
      if (isRetryableStatus(response.status) && attempt < MAX_RETRIES) {
        lastError = new Error(
          `Upload chunk failed: ${response.status} ${response.statusText}`
        );
        await delay(RETRY_BASE_DELAY_MS * Math.pow(2, attempt));
        continue;
      }

      // Non-retryable error
      throw new Error(
        `Upload chunk failed: ${response.status} ${response.statusText}`
      );
    } catch (error) {
      lastError = error;
      if ((isRetryableError(error) || isRetryableStatus(error.status)) && attempt < MAX_RETRIES) {
        await delay(RETRY_BASE_DELAY_MS * Math.pow(2, attempt));
        continue;
      }
      if (attempt === MAX_RETRIES) break;
      throw error;
    }
  }

  throw lastError;
}

/**
 * Query how many bytes GCS has received so far for a resumable session.
 * Used to resume an interrupted upload.
 *
 * @returns {{ complete: boolean, bytesReceived: number }}
 */
async function queryUploadProgress(uri, total) {
  for (let attempt = 0; ; attempt++) {
    try {
      const response = await fetch(uri, {
        method: "PUT",
        headers: {
          "Content-Range": `bytes */${total}`,
        },
      });

      return readUploadStatus(response, total);
    } catch (error) {
      if (attempt >= MAX_RETRIES ||
          !(isRetryableError(error) || isRetryableStatus(error.status))) throw error;
      await delay(RETRY_BASE_DELAY_MS * Math.pow(2, attempt));
    }
  }
}

/**
 * Upload a file to GCS using the resumable upload protocol.
 *
 * @param {File}     file       - The File object to upload.
 * @param {string}   uploadUri  - GCS resumable session URI (from backend).
 * @param {Object}   [options]
 * @param {Function} [options.onProgress] - Called with { loaded, total, percent }.
 * @returns {Promise<void>} Resolves when the upload is complete.
 */
export async function chunkedUpload(file, uploadUri, { onProgress } = {}) {
  const total = file.size;
  if (total === 0) throw new Error("Cannot upload an empty file.");

  const chunkSize = getChunkSize(total);

  // Use the same resumable/retry handling for small and large files.
  const initial = await queryUploadProgress(uploadUri, total);
  let offset = initial.bytesReceived;
  let stalledAttempts = 0;

  if (initial.complete) {
    onProgress?.({ loaded: total, total, percent: 100 });
    return; // already finished
  }

  if (offset > 0) {
    onProgress?.({
      loaded: offset,
      total,
      percent: Math.min(99, Math.round((offset / total) * 100)),
    });
  }

  while (offset < total) {
    const end = Math.min(offset + chunkSize, total) - 1;
    const chunk = file.slice(offset, end + 1);

    const result = await uploadChunkWithRetry(
      uploadUri,
      chunk,
      offset,
      end,
      total
    );

    if (result.complete) {
      onProgress?.({ loaded: total, total, percent: 100 });
      return;
    }

    // Advance to where GCS confirmed receipt
    if (result.bytesReceived < offset) {
      throw new Error("Storage did not acknowledge previously uploaded data.");
    }
    if (result.bytesReceived === offset) {
      if (stalledAttempts >= MAX_RETRIES) {
        throw new Error("Upload made no progress. Please try again.");
      }
      await delay(RETRY_BASE_DELAY_MS * Math.pow(2, stalledAttempts++));
      continue;
    }
    stalledAttempts = 0;
    offset = result.bytesReceived;

    onProgress?.({
      loaded: offset,
      total,
      percent: Math.min(99, Math.round((offset / total) * 100)),
    });
  }
}
